/* eslint-disable @typescript-eslint/no-explicit-any */
import type { ComponentType } from "react"

import eq from "lodash-es/eq"
import filter from "lodash-es/filter"
import find from "lodash-es/find"
import flatMap from "lodash-es/flatMap"
import forEach from "lodash-es/forEach"
import head from "lodash-es/head"
import includes from "lodash-es/includes"
import isEmpty from "lodash-es/isEmpty"
import isNull from "lodash-es/isNull"
import map from "lodash-es/map"
import reduce from "lodash-es/reduce"
import some from "lodash-es/some"
import sortBy from "lodash-es/sortBy"
import tail from "lodash-es/tail"
import { DateTime } from "luxon"

import type { TranslationObj } from "$/utils/lang"

import { type TParticipantHoldSession } from "@/3514/components"
import { EPath } from "@/3514/constants"
import type { TFreeResponsesQuestion } from "@/3514/entities/free-responses"
import type { TLikertQuestion, TLikertQuestionOption, TLikertSentQuestion } from "@/3514/entities/likert"
import type {
    ISelfDiagnosticAnswerBaseDto,
    ISelfDiagnosticQuestionBaseDto,
    ISelfDiagnosticQuestionBaseModel,
    ISelfDiagnosticQuestionOptionDto
} from "@/3514/types"
import { mapSelfAssessmentQuestionsToLikertQuestions } from "@/3514/utils"
import type { TGetValueFromTranslatedObjectFunction } from "@/hooks"
import { TEmptyAsyncCallback, emptyAsyncCallback } from "@/shared/types/functions"

import { EParticipantOnboardingStep as EStep } from "./participant.onboarding.api.config"
import type {
    IParticipantOnboardingCoachStepPairingModel as ICoachStepPairingModel,
    IParticipantOnboardingDiagnosticStepQueryDto as IDiagnosticStepQueryDto,
    IParticipantOnboardingDiagnosticStepQueryModel as IDiagnosticStepQueryModel,
    IParticipantOnboardingHoldsSeriesEventModel as IHoldSeriesEventModel,
    IParticipantOnboardingHoldsSeriesModel as IHoldSeriesModel,
    IParticipantOnboardingHoldsStepQueryModel as IHoldStepQueryModel,
    IParticipantOnboardingReflectionStepQueryModel as IReflectionQueryModel,
    IParticipantOnboardingStepsInfoQueryModel as IStepInfoQueryModel
} from "./participant.onboarding.api.types"
import uiConfig from "./participant.onboarding.ui.config"
import type {
    TParticipantOnboardingPageProps as TPageProps,
    TParticipantOnboardingStep as TStep
} from "./participant.onboarding.ui.types"

const { stepToPathnameMap, LEGACY_ONBOARDING_STEPS } = uiConfig

const loadersMap = {
    [EStep.Profile]: emptyAsyncCallback,
    [EStep.Video]: async (): Promise<{ default: ComponentType<TPageProps> }> =>
        await import("@/3514/pages/participant.onboarding/participant.onboarding.page.video"),
    [EStep._LegacyModules]: async (): Promise<{ default: ComponentType<TPageProps> }> =>
        await import("@/3514/pages/participant.onboarding/participant.onboarding.page.legacy.modules"),
    [EStep.Diagnostic]: async (): Promise<{ default: ComponentType<TPageProps> }> =>
        await import("@/3514/pages/participant.onboarding/participant.onboarding.page.diagnostic"),
    [EStep._LegacyDiagnosticResponses]: async (): Promise<{ default: ComponentType<TPageProps> }> =>
        await import("@/3514/pages/participant.onboarding/participant.onboarding.page.legacy.diagnostic-response"),
    [EStep.Reflection]: async (): Promise<{ default: ComponentType<TPageProps> }> =>
        await import("@/3514/pages/participant.onboarding/participant.onboarding.page.reflection"),
    [EStep.Holds]: async (): Promise<{ default: ComponentType<TPageProps> }> =>
        await import("@/3514/pages/participant.onboarding/participant.onboarding.page.holds"),
    [EStep.Coach]: async (): Promise<{ default: ComponentType<TPageProps> }> =>
        await import("@/3514/pages/participant.onboarding/participant.onboarding.page.coach"),
    [EStep.Final]: emptyAsyncCallback
}

function getNextStepNameFromAvailableAndPreviousStep(available: EStep[], previous: EStep[]): EStep {
    return find<EStep>(available, (step: EStep): boolean => !includes(previous, step))
}

export default {
    getNextPagePreloader(nextStepName: EStep): TEmptyAsyncCallback {
        return loadersMap[nextStepName] as TEmptyAsyncCallback
    },
    getAllStepsFromQueryModel(queryModel: IStepInfoQueryModel, isLoaded: boolean): TStep[] {
        if (isEmpty(queryModel) || !isLoaded) return []

        const stepList: TStep[] = []

        forEach<EStep>(
            queryModel.availableSteps,
            (step: EStep, idx: number): void =>
                void stepList.push({
                    name: step,
                    url: stepToPathnameMap[step],
                    order: idx + 1,
                    isCompleted: some<EStep>(queryModel.previousSteps, (s: EStep): boolean => eq(s, step)),
                    isVisibleInStepper: !includes<EStep>(LEGACY_ONBOARDING_STEPS, step)
                })
        )

        return stepList
    },
    getNextStepNameFromAvailableAndPreviousStep,
    getCurrentStepUrlFromQueryModel(queryModel: IStepInfoQueryModel): typeof EPath {
        if (isEmpty(queryModel)) return null

        const currentStep: EStep = getNextStepNameFromAvailableAndPreviousStep(
            queryModel.availableSteps,
            queryModel.previousSteps
        )

        return stepToPathnameMap[currentStep]
    },
    diagnostic: {
        buildLikertList(
            data: IDiagnosticStepQueryModel[],
            translator: TGetValueFromTranslatedObjectFunction
        ): TLikertQuestion[] {
            return flatMap<ISelfDiagnosticQuestionBaseModel, TLikertQuestion>(
                flatMap<IDiagnosticStepQueryModel, ISelfDiagnosticQuestionBaseModel>(
                    data,
                    (dto: IDiagnosticStepQueryModel): ISelfDiagnosticQuestionBaseModel[] => dto.questionList
                ),
                (q: ISelfDiagnosticQuestionBaseModel, idx: number): TLikertQuestion[] =>
                    mapSelfAssessmentQuestionsToLikertQuestions<TGetValueFromTranslatedObjectFunction>(
                        [q],
                        translator,
                        { order: idx, withPrompt: false, withFreeResponse: false }
                    )
            )
        },
        buildAnswerList(
            diagnostic: IDiagnosticStepQueryDto,
            sentQuestions: TLikertSentQuestion[],
            translator: TGetValueFromTranslatedObjectFunction
        ): ISelfDiagnosticAnswerBaseDto[] {
            return filter<ISelfDiagnosticAnswerBaseDto>(
                map(
                    diagnostic.question_list,
                    (question: ISelfDiagnosticQuestionBaseDto): ISelfDiagnosticAnswerBaseDto | null => {
                        const sentQuestion: TLikertSentQuestion = find<TLikertSentQuestion>(sentQuestions, {
                            questionId: question.uuid
                        })

                        if (!sentQuestion) return null

                        const option: ISelfDiagnosticQuestionOptionDto = find<ISelfDiagnosticQuestionOptionDto>(
                            question.options,
                            {
                                uuid: sentQuestion.optionId
                            }
                        )

                        return {
                            question_uuid: question.uuid,
                            answer: isEmpty(option?.response) ? String("") : translator(option.response),
                            option_uuid: sentQuestion.optionId
                        }
                    }
                ),
                (item: ISelfDiagnosticAnswerBaseDto): item is ISelfDiagnosticAnswerBaseDto => !isNull(item)
            )
        }
    },
    reflection: {
        mapReflectionQuestionsToFreeResponsesQuestions(
            models: IReflectionQueryModel[],
            { translator }: { translator: TGetValueFromTranslatedObjectFunction }
        ): TFreeResponsesQuestion[] {
            return isEmpty(models)
                ? []
                : map<IReflectionQueryModel, TFreeResponsesQuestion>(
                      models,
                      (m: IReflectionQueryModel): TFreeResponsesQuestion => ({
                          initialState: {
                              answer: String(""),
                              id: m.questionData.uuid,
                              question: translator(m.questionData.question),
                              placeholder: translator(m.questionData.prompt)! || undefined,
                              isRequired: m.questionData.isResponseRequired
                          },
                          submittedState: null
                      })
                  )
        }
    },
    holds: {
        getHoldSessionsFromHoldsQueryModel(queryModel: IHoldStepQueryModel): Record<string, TParticipantHoldSession[]> {
            return reduce<IHoldSeriesModel, Record<string, TParticipantHoldSession[]>>(
                queryModel.series,
                (
                    acc: Record<string, TParticipantHoldSession[]>,
                    series: IHoldSeriesModel
                ): Record<string, TParticipantHoldSession[]> => ({
                    ...acc,
                    [series.id]: map<IHoldSeriesEventModel, TParticipantHoldSession>(
                        sortBy<IHoldSeriesEventModel>(series.events, "enrollmentRank"),
                        (e: IHoldSeriesEventModel): TParticipantHoldSession => ({
                            startDate: e.dateTime,
                            endDate: DateTime.fromISO(e.dateTime).plus({ minutes: e.durationInMinutes }).toISO()
                        })
                    )
                }),
                {}
            )
        },
        firstSessionGetter(sessions: TParticipantHoldSession[], _setIndex: number): TParticipantHoldSession {
            return head<TParticipantHoldSession>(sessions)
        },
        restSessionsGetter(sessions: TParticipantHoldSession[], _setIndex: number): TParticipantHoldSession[] {
            return tail<TParticipantHoldSession>(sessions)
        }
    },
    coach: {
        buildLikertList(
            data: ICoachStepPairingModel[],
            translator: TGetValueFromTranslatedObjectFunction
        ): TLikertQuestion[] {
            return isEmpty(data)
                ? []
                : map<ICoachStepPairingModel, TLikertQuestion>(
                      data,
                      (model: ICoachStepPairingModel): TLikertQuestion => ({
                          initialState: {
                              id: String(model.id),
                              content: translator(model.question),
                              order: model.order,
                              isNotApplicable: false,
                              notApplicableLabel: String(""),
                              notApplicablePrompt: String(""),
                              options: isEmpty(model.answerOption)
                                  ? []
                                  : map<TranslationObj, TLikertQuestionOption>(
                                        model.answerOption.optionsLabels,
                                        (label: TranslationObj, index: number): TLikertQuestionOption => ({
                                            index,
                                            uuid: String(`${model.answerOption.id}-${index}`),
                                            label: translator(label),
                                            isFreeResponseAllowed: false,
                                            freeResponsePrompt: undefined,
                                            freeResponsePlaceholder: undefined,
                                            wasSelected: false
                                        })
                                    )
                          },
                          submittedState: null
                      })
                  )
        }
    }
}
